import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import './BlackOps.css';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import OrderDesign from './OrderDesign';
import './components/OrderSidebarStyleCard.css';
import OrderSidebarOrderList from './components/OrderSidebarOrderList';
import {
  blackOpsOperativeGroupCount,
  blackOpsOperativeGroupKey,
} from './utils/orderSidebarDisplay';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { TutorialMessageBox, TutorialReplayButton, useMenuTutorialReplay } from './tutorial/index';
import { API_URL, WS_URL } from './config/apiConfig';
import AchievementUnlockedToast from './notifications/AchievementUnlockedToast';
import { usePendingAchievementToasts } from './hooks/usePendingAchievementToasts';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import { BATTLE_OVERVIEW_MODE_STICKER_KEYS } from './battleOverview/modeStickerKeys';
import { useDismissBattleOverviewModeSticker } from './battleOverview/useDismissBattleOverviewModeSticker';
import { buildFlashlightSwatchBackground, normalizeFlashlightColorForDisplay } from './constants/flashlightColors';
import { normalizeTitlesApiLang } from './utils/battleTitleLocalization';
import MenuBackButton from './components/MenuBackButton';
import MenuPaintedButton from './components/MenuPaintedButton';
import { BLACK_OPS_SEND_BUTTON_PATH, BLACK_OPS_COMPLETE_BUTTON_PATH, BLACK_OPS_COMPLETE_BUTTON_SURFACE_FILTER, BLACK_OPS_RESET_BUTTON_PATH, BLACK_OPS_COMPOSITION_BUTTON_PATH, BATTLE_UI_END_TURN_BUTTON_PATH, MENU_PAINTED_SURFACE_FILTER } from './config/welcomeAssets';
import './StrategyModal.css';

const BLACK_OPS_LAYOUT_CSS_VARS = [
  '--bo-panel-width',
  '--bo-mission-width',
  '--bo-mission-height',
  '--bo-box-scale',
  '--bo-center-left',
  '--bo-middle-offset',
  '--bo-side-offset-x',
  '--bo-side-offset-y',
];

function clearBlackOpsLayoutVars(root = document.documentElement) {
  if (!root) {
    throw new Error('clearBlackOpsLayoutVars: root element is required');
  }
  BLACK_OPS_LAYOUT_CSS_VARS.forEach((name) => root.style.removeProperty(name));
}

/** API rows: classify supply runs using English canonical name or explicit flag (not localized display). */
function isBlackOpsSupplyRunMission(m) {
  if (m?.is_supply_run === true) return true;
  const en = m?.location_name_en;
  return typeof en === 'string' && en.startsWith('Supply run (');
}

/** @param {import('i18next').TFunction} t */
function translateBlackOpsDifficulty(t, raw) {
  const key = String(raw || '').trim().toLowerCase();
  if (key !== 'dangerous' && key !== 'risky' && key !== 'easy') {
    throw new Error(`BlackOps: unknown mission difficulty: ${JSON.stringify(raw)}`);
  }
  return t(`blackOps.difficulty.${key}`);
}

function BlackOps({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isLeavingRef = useRef(false);
  const [isExiting, setIsExiting] = useState(false);
  const { t, i18n: i18nInstance } = useTranslation();

  const getBlackOpsUiLang = () =>
    normalizeTitlesApiLang(i18nInstance.language || i18nInstance.resolvedLanguage || 'en-us');

  const blackOpsAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('BlackOps: auth token missing');
    }
    const lang = getBlackOpsUiLang();
    return {
      Authorization: `Bearer ${token}`,
      'Accept-Language': lang,
    };
  };

  const blackOpsUrlWithLang = (relativePath) => {
    const lang = getBlackOpsUiLang();
    const sep = relativePath.includes('?') ? '&' : '?';
    return `${API_URL}${relativePath}${sep}lang=${encodeURIComponent(lang)}`;
  };
  
  // Play blackops music
  useMusic('/music/blackops.mp3');
  const { current: pendingToast, dismissCurrent: dismissPendingToast } = usePendingAchievementToasts();
  const [achievementUnlocked, setAchievementUnlocked] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([null, null, null]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [selectedBoxIndex, setSelectedBoxIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [maxIntel, setMaxIntel] = useState(15);
  const [currentIntel, setCurrentIntel] = useState(15);
  const [maxIntelPerMission, setMaxIntelPerMission] = useState(0);
  const [activeMissionCount, setActiveMissionCount] = useState(0);
  const [maxActiveMissions, setMaxActiveMissions] = useState(3);
  const [missionLengthSeconds, setMissionLengthSeconds] = useState(20); // Will be updated from config
  const [missionBoxes, setMissionBoxes] = useState([
    { id: 0, location: '', difficulty: '', timer: null, timerStart: null, active: false, completed: false, missionId: null, missionLength: 20, outcome: null, operatives: [] },
    { id: 1, location: '', difficulty: '', timer: null, timerStart: null, active: false, completed: false, missionId: null, missionLength: 20, outcome: null, operatives: [] },
    { id: 2, location: '', difficulty: '', timer: null, timerStart: null, active: false, completed: false, missionId: null, missionLength: 20, outcome: null, operatives: [] }
  ]);
  const [supplyRunMissions, setSupplyRunMissions] = useState([]);
  const [rewardModalOpen, setRewardModalOpen] = useState(false);
  const [missionRewards, setMissionRewards] = useState([]);
  const [hasLegendaryReward, setHasLegendaryReward] = useState(false);
  const [intelReward, setIntelReward] = useState(0);
  const [resourceReward, setResourceReward] = useState(null);
  const [buildingReward, setBuildingReward] = useState(null);
  const [billyBucksReward, setBillyBucksReward] = useState(null);
  const [flashlightReward, setFlashlightReward] = useState(null);
  const [revealedRewards, setRevealedRewards] = useState([]);
  const videoRefs = React.useRef([]);
  const [missionOutcomeMessage, setMissionOutcomeMessage] = useState(null);
  const [woundedUnits, setWoundedUnits] = useState([]);
  const fetchOrdersErrorCountRef = React.useRef(0);
  const completedMissionIdsRef = React.useRef(new Set()); // Track recently completed mission IDs to prevent them from reappearing
  const completingBoxIdsRef = React.useRef(new Set()); // Track boxes currently being completed to prevent double-clicks
  const [savedCompositions, setSavedCompositions] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [compositionName, setCompositionName] = useState('');
  const [showTutorialBlackOps, setShowTutorialBlackOps] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  useDismissBattleOverviewModeSticker(user, BATTLE_OVERVIEW_MODE_STICKER_KEYS.blackOps);
  const { isReplayRequest: isReplayingBlackOpsTutorial } = useMenuTutorialReplay('black_ops', {
    enabled: Boolean(user) && !loading,
    onReplay: () => setShowTutorialBlackOps(true),
  });

  useEffect(() => {
    if (user && !loading && shouldShowTutorial('black_ops')) {
      const timer = setTimeout(() => setShowTutorialBlackOps(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, shouldShowTutorial]);

  // Responsive layout state - calculated via JS for precise control
  const [layout, setLayout] = useState(null);

  // Calculate responsive layout based on viewport
  useEffect(() => {
    const calculateLayout = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const root = document.documentElement;
      
      // Only apply responsive layout for screens under 1400px
      if (vw > 1400) {
        setLayout(null); // Use CSS defaults
        clearBlackOpsLayoutVars(root);
        return;
      }

      // Panel width: ~22% of viewport, clamped (+ shared sidebar card extra width)
      const panelExtra = 15;
      const panelWidth = Math.min(300, Math.max(165, vw * 0.22)) + panelExtra;
      
      // Mission boxes: 10-15% of viewport, clamped
      const missionWidth = Math.min(180, Math.max(100, vw * 0.12));
      
      // Mission box height: ~25% of viewport height each (3 boxes + gaps = ~80% of vh)
      // Clamped between 60px and 120px
      const missionBoxHeight = Math.min(120, Math.max(60, vh * 0.22));
      
      // Available center space
      const margins = 40; // 20px on each side
      const availableWidth = vw - panelWidth - missionWidth - margins;
      
      // Scale factor for OrderDesign boxes (new design: 390px wide, height determined by border aspect ratio)
      // Scale to fit: 60% of viewport height, or 30% of available width
      // Using conservative height estimate - border images maintain similar aspect ratio to original
      // Reduced by 20% overall
      const newBoxWidth = 312; // New order design width (390 * 0.8)
      const newBoxHeight = 320; // Conservative height estimate (400 * 0.8)
      const boxScale = Math.min(vh * 0.55 / newBoxHeight, availableWidth * 0.28 / newBoxWidth, 1) * 0.8; // Additional 20% reduction
      
      // Offsets - middle box slightly above center, side boxes below and to sides
      const middleOffsetY = -20; // Moved down from -50
      const sideOffsetY = 50; // Side boxes below center
      const sideOffsetX = 130; // Horizontal spread (fixed, scale handles the visual spread)
      
      // Center position of the boxes container
      const centerLeft = panelWidth + margins/2 + availableWidth/2;

      // Set CSS custom properties for use in CSS
      root.style.setProperty('--bo-panel-width', `${panelWidth}px`);
      root.style.setProperty('--bo-mission-width', `${missionWidth}px`);
      root.style.setProperty('--bo-mission-height', `${missionBoxHeight}px`);
      root.style.setProperty('--bo-box-scale', boxScale);
      root.style.setProperty('--bo-center-left', `${centerLeft}px`);
      root.style.setProperty('--bo-middle-offset', `${middleOffsetY}px`);
      root.style.setProperty('--bo-side-offset-x', `${sideOffsetX}px`);
      root.style.setProperty('--bo-side-offset-y', `${sideOffsetY}px`);

      console.log('BlackOps layout calculated:', { vw, vh, panelWidth, missionWidth, missionBoxHeight, boxScale, centerLeft });

      setLayout({
        panelWidth,
        missionWidth,
        missionBoxHeight,
        boxScale,
        middleOffsetY,
        sideOffsetY,
        sideOffsetX,
        centerLeft
      });
    };

    calculateLayout();
    window.addEventListener('resize', calculateLayout);
    return () => {
      window.removeEventListener('resize', calculateLayout);
      clearBlackOpsLayoutVars(document.documentElement);
    };
  }, []);

  const exitToBattleOverview = useCallback(() => {
    if (isLeavingRef.current) {
      return;
    }
    isLeavingRef.current = true;
    clearBlackOpsLayoutVars(document.documentElement);
    flushSync(() => {
      setIsExiting(true);
    });
    navigate('/battleoverview', { replace: true });
  }, [navigate]);

  // Check if current user is player1 (for restricted features)
  const isPlayer1 = (typeof user === 'string' && user === 'player1') || 
                    (user?.username === 'player1') || 
                    (user?.id === 'player1');

  const fetchOrders = async () => {
    try {
      // Get current language from i18n and normalize it for the API
      const currentLanguage = i18nInstance.language || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      
      const res = await fetch(`${API_URL}/all-orders?lang=${encodeURIComponent(normalizedLanguage)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      // Check for authentication errors
      if (res.status === 401 || res.status === 403) {
        console.error('Authentication failed - token may have expired');
        fetchOrdersErrorCountRef.current++;
        // Don't clear orders on auth error - let user see the issue and refresh manually
        // If too many consecutive errors, stop trying to refresh
        if (fetchOrdersErrorCountRef.current > 3) {
          console.warn('Too many fetch errors - stopping automatic refresh');
        }
        return;
      }
      
      if (!res.ok) {
        console.error('Failed to fetch orders - HTTP error:', res.status, res.statusText);
        fetchOrdersErrorCountRef.current++;
        // Don't clear orders on HTTP error - keep existing orders
        return;
      }
      
      const data = await res.json();
      if (isLeavingRef.current) {
        return;
      }
      if (Array.isArray(data)) {
        // Reset error count on successful fetch
        fetchOrdersErrorCountRef.current = 0;
        
        // Filter to only show owned orders or orders with availability='Created'
        // Unowned orders are completely hidden (not grayed out)
        const ownedOrders = data.filter(order => {
          // Orders with availability='Created' are always available to everyone
          if (order.availability === 'Created') return true;
          // Use ownedCount if available, fallback to isOwned for backward compatibility
          const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 1 : 0);
          // Only show orders that have at least 1 copy owned
          return ownedCount > 0;
        });
        
        // Split orders to show each wounded and on-mission unit individually
        // This ensures all unavailable units are visible in the table
        const processedOrders = [];
        ownedOrders.forEach(order => {
          const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 1 : 0);
          const woundedCount = order.woundedCount || 0;
          const onMissionCount = order.onMissionCount || 0;
          const totalUnavailable = woundedCount + onMissionCount;
          const availableCount = ownedCount - totalUnavailable;
          const woundedAvailableAt =
            order.availableAt ||
            order.available_at ||
            order.woundedAvailableAt ||
            order.wounded_available_at ||
            null;
          
          // Always split if there are wounded or on-mission units (even if all are unavailable)
          // This ensures each wounded/on-mission unit is shown individually
          if (totalUnavailable > 0) {
            // Create individual entries for each wounded unit
            for (let i = 0; i < woundedCount; i++) {
              const woundedCopy = {
                ...order,
                isWounded: true,
                availableAt: woundedAvailableAt,
                available_at: woundedAvailableAt,
                woundedCount: 1, // Each entry represents 1 wounded unit
                onMissionCount: 0,
                isOnMission: false,
                ownedCount: 1, // Each entry represents 1 unit
                // Add index to make each entry unique
                _splitKey: `${order.id}_wounded_${i}`
              };
              processedOrders.push(woundedCopy);
            }
            
            // Create individual entries for each on-mission unit
            for (let i = 0; i < onMissionCount; i++) {
              const onMissionCopy = {
                ...order,
                isWounded: false,
                availableAt: null,
                woundedCount: 0,
                onMissionCount: 1, // Each entry represents 1 on-mission unit
                isOnMission: true,
                ownedCount: 1, // Each entry represents 1 unit
                // Add index to make each entry unique
                _splitKey: `${order.id}_onmission_${i}`
              };
              processedOrders.push(onMissionCopy);
            }
            
            // Create available copy entry (if any are available)
            if (availableCount > 0) {
              const availableCopy = {
                ...order,
                isWounded: false,
                availableAt: null,
                woundedCount: 0,
                onMissionCount: 0,
                isOnMission: false,
                ownedCount: availableCount,
                // Add a suffix to the key to make it unique
                _splitKey: `${order.id}_available`
              };
              processedOrders.push(availableCopy);
            }
          } else {
            // No wounded or on-mission units - keep original order as-is
            processedOrders.push(order);
          }
        });
        
        setAvailableOrders(processedOrders);
      } else {
        console.error('Unexpected response format:', data);
        fetchOrdersErrorCountRef.current++;
        // Don't clear orders on unexpected format - keep existing orders
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      fetchOrdersErrorCountRef.current++;
      
      // Don't clear orders on network error - keep existing orders to prevent UI breakage
      // Only clear on initial load if we have no orders yet
      if (availableOrders.length === 0 && loading) {
        setAvailableOrders([]);
      }
      
      // If too many consecutive errors, log warning
      if (fetchOrdersErrorCountRef.current > 3) {
        console.warn('Too many consecutive fetch errors - orders list may be stale');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchIntel = async () => {
    try {
      const res = await fetch(`${API_URL}/blackops/intel`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (isLeavingRef.current) {
        return;
      }
      console.log('📊 BlackOps Config fetched:', {
        currentIntel: data.currentIntel,
        maxIntel: data.maxIntel,
        maxIntelPerMission: data.maxIntelPerMission,
        missionLengthSeconds: data.missionLengthSeconds
      });
      if (data.currentIntel !== undefined) {
        setCurrentIntel(data.currentIntel);
      }
      if (data.maxIntel !== undefined) {
        setMaxIntel(data.maxIntel);
      }
      if (data.maxIntelPerMission !== undefined) {
        setMaxIntelPerMission(data.maxIntelPerMission);
      }
      if (data.missionLengthSeconds !== undefined) {
        setMissionLengthSeconds(data.missionLengthSeconds);
        // Update mission box lengths if they're still at default or if they don't have a mission yet
        setMissionBoxes(prev => prev.map(box => ({
          ...box,
          missionLength: data.missionLengthSeconds // Always use config value for display
        })));
      }
      if (data.activeMissionCount !== undefined) {
        setActiveMissionCount(data.activeMissionCount);
      }
      if (data.maxActiveMissions !== undefined) {
        setMaxActiveMissions(data.maxActiveMissions);
      }
    } catch (err) {
      console.error('Failed to fetch intel:', err);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchIntel();
    fetchCompositions();
  }, [i18nInstance.language]);

  const fetchCompositions = async () => {
    try {
      const res = await fetch(`${API_URL}/blackops/compositions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        if (isLeavingRef.current) {
          return;
        }
        setSavedCompositions(data);
      }
    } catch (err) {
      console.error('Failed to fetch compositions:', err);
    }
  };

  const handleSaveComposition = async () => {
    if (!selectedOrders[0] || !selectedOrders[1] || !selectedOrders[2]) {
      alert(t('blackOps.select3Operatives'));
      return;
    }

    if (!compositionName.trim()) {
      alert(t('blackOps.compositionNameRequired'));
      return;
    }

    try {
      const res = await fetch(`${API_URL}/blackops/save-composition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: compositionName.trim(),
          operative1_id: selectedOrders[0].id,
          operative2_id: selectedOrders[1].id,
          operative3_id: selectedOrders[2].id,
        }),
      });

      if (res.ok) {
        setSaveModalOpen(false);
        setCompositionName('');
        await fetchCompositions();
        alert(t('blackOps.compositionSaved'));
      } else {
        const error = await res.json();
        alert(error.error || t('blackOps.failedToSaveComposition'));
      }
    } catch (err) {
      console.error('Failed to save composition:', err);
      alert(t('blackOps.failedToSaveComposition'));
    }
  };

  const handleLoadComposition = async (composition) => {
    try {
      // Find the orders by ID from availableOrders
      // Look for available (not wounded, not on mission) orders first
      const findAvailableOrder = (orderId) => {
        return availableOrders.find(o => 
          o.id === orderId && 
          !o.isWounded && 
          !o.isOnMission &&
          (o.ownedCount > 0 || o.availability === 'Created')
        );
      };

      const order1 = findAvailableOrder(composition.operative1_id);
      const order2 = findAvailableOrder(composition.operative2_id);
      const order3 = findAvailableOrder(composition.operative3_id);

      if (!order1 || !order2 || !order3) {
        alert(t('blackOps.compositionNotAvailable'));
        return;
      }

      setSelectedOrders([order1, order2, order3]);
      setLoadModalOpen(false);
    } catch (err) {
      console.error('Failed to load composition:', err);
      alert(t('blackOps.failedToLoadComposition'));
    }
  };

  const handleDeleteComposition = async (compositionId, e) => {
    e.stopPropagation(); // Prevent loading when clicking delete
    if (!window.confirm(t('blackOps.confirmDeleteComposition'))) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/blackops/composition/${compositionId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (res.ok) {
        await fetchCompositions();
      } else {
        alert(t('blackOps.failedToDeleteComposition'));
      }
    } catch (err) {
      console.error('Failed to delete composition:', err);
      alert(t('blackOps.failedToDeleteComposition'));
    }
  };

  // ✅ DEV: Check if dev rewards are passed via state (for dev demo)
  useEffect(() => {
    if (location.state?.devRewards && Array.isArray(location.state.devRewards)) {
      console.log('🎁 BlackOps: Using dev rewards from state');
      setMissionRewards(location.state.devRewards);
      if (location.state.devIntel) {
        setIntelReward(location.state.devIntel);
      }
      setRewardModalOpen(true);
      // Check for legendary reward
      const hasLegendary = location.state.devRewards.some(reward => reward.rarity?.toLowerCase() === 'legendary');
      setHasLegendaryReward(hasLegendary);
    }
  }, [location.state]);

  // Ensure videos play when rewards are loaded
  useEffect(() => {
    if (rewardModalOpen && (missionRewards.length > 0 || intelReward > 0 || resourceReward || buildingReward || billyBucksReward || flashlightReward)) {
      const totalRewards = missionRewards.length + (intelReward > 0 ? 1 : 0) + (resourceReward ? 1 : 0) + (buildingReward ? 1 : 0) + (billyBucksReward ? 1 : 0) + (flashlightReward ? 1 : 0);
      for (let index = 0; index < totalRewards; index++) {
        const video = videoRefs.current[index];
        if (video && !revealedRewards[index]) {
          video.play().catch(err => {
            console.error(`Error playing video ${index}:`, err);
          });
        }
      }
    }
  }, [rewardModalOpen, missionRewards, intelReward, resourceReward, buildingReward, billyBucksReward, flashlightReward, revealedRewards]);
  
  // Close mission outcome message on click (desktop) or touch/keypress (mobile)
  useEffect(() => {
    if (!missionOutcomeMessage) return;

    const isMobile = window.innerWidth <= 1024;
    
    const handleClose = () => {
      setMissionOutcomeMessage(null);
    };

    if (isMobile) {
      // Mobile: close on touch or keypress
      document.addEventListener('touchstart', handleClose, { once: true });
      document.addEventListener('keydown', handleClose, { once: true });
      
      return () => {
        document.removeEventListener('touchstart', handleClose);
        document.removeEventListener('keydown', handleClose);
      };
    } else {
      // Desktop: close on click
      document.addEventListener('click', handleClose, { once: true });
      
      return () => {
        document.removeEventListener('click', handleClose);
      };
    }
  }, [missionOutcomeMessage]);

  useEffect(() => {
    // Set up interval to update wounded time countdown every minute
    const woundedInterval = setInterval(() => {
      // Only refresh if we haven't had too many consecutive errors
      if (fetchOrdersErrorCountRef.current <= 3) {
        fetchOrders();
      }
    }, 60000); // Every minute
    
    // Fetch intel every minute
    const intelInterval = setInterval(() => {
      fetchIntel();
    }, 60000); // 60 seconds
    
    // Initial mission list: handled by useEffect on i18nInstance.language (includes first mount)
    
    // Check for completed missions every 30 seconds
    const completedCheckInterval = setInterval(() => {
      checkCompletedMissions();
    }, 30000);
    
    return () => {
      clearInterval(woundedInterval);
      clearInterval(intelInterval);
      clearInterval(completedCheckInterval);
    };
  }, []);

  const checkCompletedMissions = async () => {
    try {
      const res = await fetch(`${API_URL}/blackops/completed-missions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (isLeavingRef.current) {
        return;
      }
      
      if (Array.isArray(data) && data.length > 0) {
        // Check if any completed missions match our active boxes
        setMissionBoxes(prev => prev.map(box => {
          // Skip boxes that were just completed - don't overwrite their state
          if (box._justCompleted) {
            return box;
          }
          
          // Skip missions that are already in the completed set (already processed)
          if (box.missionId && completedMissionIdsRef.current.has(box.missionId)) {
            return box;
          }
          
          if (box.active && box.missionId) {
            const completed = data.find(m => m.id === box.missionId);
            if (completed && completed.outcome) {
              // Mission completed - keep timer at 0 and mark as completed
              return {
                ...box,
                active: true, // Keep active so it shows as completed
                completed: true,
                timer: 0
              };
            }
          }
          return box;
        }));
        
        // Also check Supply Run missions
        setSupplyRunMissions(prev => prev.map(box => {
          // Skip boxes that were just completed - don't overwrite their state
          if (box._justCompleted) {
            return box;
          }
          
          // Skip missions that are already in the completed set (already processed)
          if (box.missionId && completedMissionIdsRef.current.has(box.missionId)) {
            return box;
          }
          
          if (box.active && box.missionId) {
            const completed = data.find(m => m.id === box.missionId);
            if (completed && completed.outcome) {
              // Mission completed - keep timer at 0 and mark as completed
              return {
                ...box,
                active: true, // Keep active so it shows as completed
                completed: true,
                timer: 0
              };
            }
          }
          return box;
        }));
      }
    } catch (err) {
      console.error('Failed to check completed missions:', err);
    }
  };

  const fetchActiveMissions = async () => {
    try {
      const res = await fetch(blackOpsUrlWithLang('/blackops/active-missions'), {
        headers: blackOpsAuthHeaders(),
      });
      const data = await res.json();
      if (isLeavingRef.current) {
        return;
      }
      
      console.log('Fetched active missions (sent missions):', data);
      
      if (Array.isArray(data) && data.length > 0) {
        // Filter out any missions that were recently completed (they shouldn't be in the list, but be defensive)
        const filteredData = data.filter(m => !completedMissionIdsRef.current.has(m.id));
        
        if (filteredData.length !== data.length) {
          console.warn(`[BlackOps] Filtered out ${data.length - filteredData.length} recently completed missions from active missions list`);
        }
        
        // Update mission boxes with active missions (missions that have been sent)
        // These missions have send_time IS NOT NULL
        setMissionBoxes(prev => {
          // Track which missions are already assigned to boxes to prevent duplicate assignments
          const assignedMissionIds = new Set();
          
          const updated = prev.map((box, index) => {
            // Check if a completed mission was deleted (even if _justCompleted is set)
            // This ensures completed missions disappear immediately
            if (box.active && box.missionId && box.completed) {
              const missionStillExists = filteredData.some(m => m.id === box.missionId);
              if (!missionStillExists) {
                // Mission was completed and deleted - reset box to show new pre-generated mission
                // (fetchLocations will update it with the pre-generated mission)
                console.log(`[BlackOps] Mission ${box.missionId} was completed and deleted, resetting box ${box.id}`);
                return {
                  ...box,
                  active: false,
                  completed: false,
                  timer: null,
                  timerStart: null,
                  outcome: null,
                  operatives: [],
                  missionId: null,
                  location: '', // Clear location
                  difficulty: '' // Clear difficulty
                };
              }
            }
            
            // Skip boxes that were just completed - don't overwrite their state at all
            if (box._justCompleted) {
              // Still track the missionId if it exists to prevent duplicate assignments
              if (box.missionId) {
                assignedMissionIds.add(box.missionId);
              }
              // Also verify that the mission in this box isn't in the active missions list
              // (it shouldn't be, since completed missions are deleted, but double-check)
              if (box.missionId && data.some(m => m.id === box.missionId)) {
                console.warn(`[BlackOps] Box ${box.id} has _justCompleted flag but mission ${box.missionId} is still in active missions list - this shouldn't happen`);
              }
              return box;
            }
            
            // Try to find by missionId first (for existing active missions)
            // But skip if this mission was recently completed
            let activeMission = null;
            if (box.missionId && !completedMissionIdsRef.current.has(box.missionId)) {
              activeMission = filteredData.find(m => m.id === box.missionId);
            }
            
            // If found by missionId, track it
            if (activeMission) {
              assignedMissionIds.add(activeMission.id);
            }
            
            // CRITICAL FIX: Only match by position if the box doesn't have a missionId at all
            // This prevents overwriting pre-generated missions that haven't been sent yet
            // Pre-generated missions have missionId set but active: false
            // We should NEVER assign an active mission to a box that has a different pre-generated mission
            if (!activeMission && !box.missionId && index < filteredData.length) {
              const candidateMission = filteredData[index];
              // Only assign if this mission hasn't been assigned to another box
              // AND it wasn't recently completed
              if (candidateMission && !assignedMissionIds.has(candidateMission.id) && !completedMissionIdsRef.current.has(candidateMission.id)) {
                activeMission = candidateMission;
                assignedMissionIds.add(candidateMission.id);
              }
            }
            
            if (activeMission) {
              // Use database-calculated completion_time to ensure timer matches database completion check
              // This prevents timer reaching 0 before database thinks mission is complete
              // Ensure UTC timezone handling: parse completion_time as UTC and compare with UTC now
              const completionTime = new Date(activeMission.completion_time);
              // Use UTC time for comparison to match backend UTC calculations
              const now = new Date();
              // Both getTime() return UTC milliseconds, so this comparison is timezone-independent
              const actualRemaining = Math.max(0, completionTime.getTime() - now.getTime());
              const isCompleted = activeMission.is_completed || actualRemaining <= 0;
              
              // Log for debugging timer sync issues
              if (box.missionId === activeMission.id && box.timer !== null && Math.abs(box.timer - actualRemaining) > 5000) {
                console.log(`[BlackOps] Timer sync: Frontend had ${box.timer}ms, Database says ${actualRemaining}ms, syncing to database`);
              }
              
              // Extract operatives from backend - either from operatives array or from unit1_id, unit2_id, unit3_id
              let operatives = [];
              if (activeMission.operatives && Array.isArray(activeMission.operatives)) {
                operatives = activeMission.operatives;
              } else if (activeMission.unit1_id || activeMission.unit2_id || activeMission.unit3_id) {
                // Fallback: construct array from individual unit IDs
                operatives = [
                  activeMission.unit1_id,
                  activeMission.unit2_id,
                  activeMission.unit3_id
                ].filter(id => id !== null && id !== undefined);
              }
              
              console.log(`Updating box ${box.id} (index ${index}) with active mission ${activeMission.id}: remaining=${actualRemaining}ms (${formatTimer(actualRemaining)}), completed=${isCompleted}, operatives=${operatives.join(',')}`);
              
              // Use missionLengthSeconds from server response if available, otherwise fall back to state
              const missionLength = activeMission.missionLengthSeconds || missionLengthSeconds;
              
              return {
                ...box,
                location: activeMission.location_name || box.location || t('blackOps.unknownLocation'),
                locationNameEn: activeMission.location_name_en ?? box.locationNameEn ?? null,
                isSupplyRun: activeMission.is_supply_run === true || box.isSupplyRun === true,
                difficulty: activeMission.difficulty || box.difficulty || 'Risky',
                active: true,
                completed: isCompleted,
                timer: isCompleted ? 0 : actualRemaining, // Always set to 0 if completed
                timerStart: new Date(activeMission.send_time).getTime(),
                missionId: activeMission.id,
                missionLength: missionLength, // Use config value from server response
                outcome: activeMission.outcome,
                operatives: operatives.length > 0 ? operatives : (box.operatives || []) // Use operatives from backend or preserve existing
              };
            }
            
            // Otherwise keep it as is (it might be a pre-generated mission that hasn't been sent yet)
            return box;
          });
          return updated;
        });
      } else {
        console.log('No active missions (sent missions) found - all missions are pre-generated');
      }
    } catch (err) {
      console.error('Failed to fetch active missions:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(blackOpsUrlWithLang('/blackops/missions'), {
        headers: blackOpsAuthHeaders(),
      });
      const data = await res.json();
      if (isLeavingRef.current) {
        return;
      }
      console.log('Fetched pre-generated missions (not yet sent) from API:', data);
      console.log('Number of pre-generated missions:', data?.length || 0);
      
      if (Array.isArray(data) && data.length > 0) {
        // Separate regular missions from Supply Run missions (use canonical English / flag, not localized title)
        const regularMissions = data.filter((m) => !isBlackOpsSupplyRunMission(m));
        const supplyRuns = data.filter((m) => isBlackOpsSupplyRunMission(m));
        
        // Only log mission counts in development mode to reduce console noise
        if (process.env.NODE_ENV === 'development') {
          console.log(`Found ${regularMissions.length} regular missions and ${supplyRuns.length} Supply Run missions`);
        }
        
        // Warn if we have fewer than 3 regular missions (only log once, not on every render)
        if (regularMissions.length < 3) {
          // Only warn in development mode - this is likely a backend/database issue
          if (process.env.NODE_ENV === 'development') {
            if (regularMissions.length === 0) {
              console.warn(`[BlackOps] No regular missions found. Backend should create 3 regular missions.`);
            } else {
              console.warn(`[BlackOps] Only ${regularMissions.length} regular missions found, expected 3.`);
            }
          }
        }
        
        // Update regular mission boxes (first 3)
        setMissionBoxes(prev => prev.map((box, index) => {
          // If this box already has an active mission, don't overwrite it
          if (box.active) {
            console.log(`Box ${box.id} already has active mission ${box.missionId}, skipping pre-generated mission update`);
            return box;
          }
          
          // Skip boxes that were just completed - don't overwrite their state
          if (box._justCompleted) {
            return box;
          }
          
          // Otherwise, use the pre-generated mission for this box
          const mission = regularMissions[index];
          if (mission) {
            // Ensure location_name is never null/undefined/empty
            const locationName = mission.location_name && mission.location_name.trim() 
              ? mission.location_name.trim() 
              : t('blackOps.unknownLocation');
            
            console.log(`Setting pre-generated mission for box ${box.id}: missionId=${mission.id}, location=${locationName}`);
            // Use missionLengthSeconds from server response if available, otherwise fall back to state
            const missionLength = mission.missionLengthSeconds || missionLengthSeconds;
            return {
              ...box,
              location: locationName,
              locationNameEn: mission.location_name_en ?? null,
              locationId: mission.location_id ?? null,
              isSupplyRun: false,
              difficulty: mission.difficulty || 'Risky',
              missionId: mission.id || null,
              missionLength: missionLength, // Use config value from server response
              active: false,
              completed: false,
              timer: null,
              timerStart: null,
              outcome: null
            };
          } else {
            // No mission available for this box - silently handle (this is expected if no regular missions exist)
            // Don't log warnings as this is a backend issue, not a frontend bug
          }
          return box;
        }));
        
        // Update Supply Run missions - merge with existing instead of replacing
        const missionLength = data[0]?.missionLengthSeconds || missionLengthSeconds;
        setSupplyRunMissions(prev => {
          // Create maps for quick lookup
          const existingBoxesByMissionId = new Map();
          const existingBoxesByLocationId = new Map();
          prev.forEach(box => {
            if (box.missionId) {
              existingBoxesByMissionId.set(box.missionId, box);
            }
            if (box.locationId != null) {
              existingBoxesByLocationId.set(String(box.locationId), box);
            }
          });
          
          // Process each Supply Run mission from the API
          // First, deduplicate by missionId to prevent duplicate missions
          const uniqueMissionsByMissionId = new Map();
          supplyRuns.forEach(mission => {
            if (mission.id && !uniqueMissionsByMissionId.has(mission.id)) {
              uniqueMissionsByMissionId.set(mission.id, mission);
            }
          });
          const deduplicatedMissions = Array.from(uniqueMissionsByMissionId.values());
          
          // Track used IDs as we build to ensure uniqueness
          const usedIds = new Set(prev.map(b => b.id));
          
          // Then, deduplicate by location (keep the first one encountered for each location)
          // This handles cases where the backend creates multiple missions with the same location
          const missionsByLocationId = new Map();
          deduplicatedMissions.forEach(mission => {
            const lid = mission.location_id != null ? String(mission.location_id) : null;
            const dedupeKey = lid || mission.location_name_en || mission.location_name || '';
            if (!dedupeKey) return;
            if (!missionsByLocationId.has(dedupeKey)) {
              missionsByLocationId.set(dedupeKey, mission);
            } else if (process.env.NODE_ENV === 'development') {
              console.warn(`[Supply Runs] Duplicate supply location key ${dedupeKey} (missionId: ${mission.id}), keeping first occurrence`);
            }
          });
          const finalMissions = Array.from(missionsByLocationId.values());
          
          const updatedBoxes = finalMissions
            .map((mission, index) => {
              const location = mission.location_name || t('blackOps.unknownLocation');
              
              // First, check if we already have a box with this missionId
              let existingBox = existingBoxesByMissionId.get(mission.id);
              
              // If not found by missionId, check by location (in case missionId changed)
              if (!existingBox && mission.location_id != null) {
                existingBox = existingBoxesByLocationId.get(String(mission.location_id));
              }
              
              if (existingBox) {
                // Box already exists - update it but preserve state if it's active or just completed
                if (existingBox.active || existingBox._justCompleted) {
                  // Don't overwrite active missions or recently completed ones
                  return existingBox;
                }
                
                // Update the existing box with new data
                return {
                  ...existingBox,
                  location: location,
                  locationNameEn: mission.location_name_en ?? existingBox.locationNameEn ?? null,
                  locationId: mission.location_id ?? existingBox.locationId ?? null,
                  difficulty: mission.difficulty || 'Dangerous',
                  missionId: mission.id || null,
                  missionLength: mission.missionLengthSeconds || missionLength,
                  // Preserve timer and other state if they exist
                  timer: existingBox.timer,
                  timerStart: existingBox.timerStart,
                  active: existingBox.active,
                  completed: existingBox.completed,
                  outcome: existingBox.outcome,
                  operatives: existingBox.operatives || [],
                  isSupplyRun: true
                };
              } else {
                // New mission - create a new box
                // Use missionId as the base for ID to ensure uniqueness
                // If missionId is not available, find the next available ID (starting from 100)
                let newId;
                if (mission.id) {
                  // Use missionId + 10000 to ensure it doesn't conflict with regular mission IDs (0-2)
                  newId = 10000 + mission.id;
                } else {
                  // Fallback: find the next available ID (starting from 100)
                  const maxId = prev.length > 0 
                    ? Math.max(...prev.map(b => b.id), 99)
                    : 99;
                  newId = maxId + 1;
                }
                
                // Ensure ID is truly unique by checking against used IDs
                while (usedIds.has(newId)) {
                  newId++;
                }
                usedIds.add(newId); // Mark this ID as used
                
                return {
                  id: newId,
                  location: location,
                  locationNameEn: mission.location_name_en ?? null,
                  locationId: mission.location_id ?? null,
                  difficulty: mission.difficulty || 'Dangerous',
                  timer: null,
                  timerStart: null,
                  active: false,
                  completed: false,
                  missionId: mission.id || null,
                  missionLength: mission.missionLengthSeconds || missionLength,
                  outcome: null,
                  operatives: [],
                  isSupplyRun: true
                };
              }
            });
          
          // Remove boxes that no longer exist in the database (missionId not in supplyRuns)
          // But keep boxes that are active or just completed (they'll be handled separately)
          const supplyRunMissionIds = new Set(supplyRuns.map(m => m.id));
          const updatedBoxesById = new Map(updatedBoxes.map(b => [b.missionId, b]));
          
          // Add any boxes from prev that are active or just completed but not in updatedBoxes
          prev.forEach(box => {
            // If box is active or just completed, and not already in updatedBoxes, add it
            if ((box.active || box._justCompleted) && box.missionId && !updatedBoxesById.has(box.missionId)) {
              updatedBoxes.push(box);
            }
          });
          
          // Filter out boxes that no longer exist in the database (unless they're active or just completed)
          const filteredBoxes = updatedBoxes.filter(box => {
            // Always keep boxes that are active or just completed
            if (box.active || box._justCompleted) {
              return true;
            }
            // Keep boxes that have a missionId that exists in the database
            if (box.missionId && supplyRunMissionIds.has(box.missionId)) {
              return true;
            }
            // Remove boxes that don't have a missionId or are no longer in the database
            return false;
          });
          
          // Log the update (only in development mode to reduce console noise)
          if (process.env.NODE_ENV === 'development') {
            console.log(`Updated Supply Run missions (${finalMissions.length} unique from ${supplyRuns.length} API missions)`);
          }
          
          return filteredBoxes;
        });
      } else {
        console.error('Invalid missions data from API:', data);
        console.error('Data type:', typeof data);
        console.error('Is array?', Array.isArray(data));
        // Don't set fallback - let the user see the error
        alert(t('blackOps.failedToLoadMissions'));
      }
    } catch (err) {
      console.error('Failed to fetch missions:', err);
      console.error('Error details:', err.message, err.stack);
      // Don't set fallback - show error to user
      alert(t('blackOps.failedToFetchMissions', { error: err.message }));
    }
  };

  useEffect(() => {
    fetchActiveMissions().then(() => fetchLocations());
  }, [i18nInstance.language]);

  // Timer effect - counts down client-side, syncs with server on refresh
  useEffect(() => {
    const interval = setInterval(() => {
      if (isLeavingRef.current) {
        return;
      }
      // Update regular mission boxes
      setMissionBoxes(prev => prev.map(box => {
        if (box.active && !box.completed && box.timer !== null && box.timer !== undefined) {
          // Calculate remaining time from completion time
          if (box.timer > 0) {
            const newTimer = Math.max(0, box.timer - 1000);
            if (newTimer <= 0) {
              // Timer reached 0, mission is completed - keep timer at 0
              console.log(`Mission ${box.missionId} timer reached 0`);
              return { ...box, timer: 0, completed: true };
            }
            return { ...box, timer: newTimer };
          } else if (box.timer === 0) {
            // Already at 0, mark as completed and ensure timer stays at 0
            return { ...box, timer: 0, completed: true };
          }
        } else if (box.active && box.completed) {
          // Mission is completed - ensure timer stays at 0
          return { ...box, timer: 0, completed: true };
        }
        return box;
      }));
      
      // Update Supply Run mission boxes
      setSupplyRunMissions(prev => prev.map(box => {
        if (box.active && !box.completed && box.timer !== null && box.timer !== undefined) {
          // Calculate remaining time from completion time
          if (box.timer > 0) {
            const newTimer = Math.max(0, box.timer - 1000);
            if (newTimer <= 0) {
              // Timer reached 0, mission is completed - keep timer at 0
              console.log(`Supply Run Mission ${box.missionId} timer reached 0`);
              return { ...box, timer: 0, completed: true };
            }
            return { ...box, timer: newTimer };
          } else if (box.timer === 0) {
            // Already at 0, mark as completed and ensure timer stays at 0
            return { ...box, timer: 0, completed: true };
          }
        } else if (box.active && box.completed) {
          // Mission is completed - ensure timer stays at 0
          return { ...box, timer: 0, completed: true };
        }
        return box;
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Check for completed missions when timer reaches 0
  useEffect(() => {
    const hasCompletedTimer = missionBoxes.some(box => box.active && box.timer !== null && box.timer <= 0) ||
                              supplyRunMissions.some(box => box.active && box.timer !== null && box.timer <= 0);
    if (hasCompletedTimer) {
      checkCompletedMissions();
    }
  }, [missionBoxes, supplyRunMissions]);

  const handleBoxClick = (index) => {
    // If there's a pending order, place it in this box
    if (pendingOrder) {
      // Check if we can afford the pending order
      if (!canAffordOrder(pendingOrder)) {
        alert(t('blackOps.cannotAfford', { name: pendingOrder.name, cost: pendingOrder.cost, available: availableIntelForMission }));
        setPendingOrder(null);
        return;
      }
      
      // Check if order is wounded
      if (pendingOrder.isWounded) {
        const rawAvailableAt = getWoundedAvailableAt(pendingOrder);
        const availableAt = rawAvailableAt ? new Date(rawAvailableAt) : null;
        const now = new Date();
        const remainingMs = availableAt ? availableAt.getTime() - now.getTime() : 0;
        const remainingMins = Math.ceil(remainingMs / (1000 * 60));
        const timeStr = `${remainingMins} ${remainingMins !== 1 ? t('blackOps.minutes') : t('blackOps.minute')}`;
        alert(t('blackOps.cannotAssignWounded', { name: pendingOrder.name, time: timeStr }));
        setPendingOrder(null);
        return;
      }
      
      // Check if order is on an active mission
      // If order has been split (has _splitKey), only check order.isOnMission
      // Otherwise, also check missionBoxes for backward compatibility
      if (pendingOrder.isOnMission) {
        alert(t('blackOps.cannotAssignOnMission', { name: pendingOrder.name }));
        setPendingOrder(null);
        return;
      }
      // Only check missionBoxes if this order hasn't been split
      // (split orders already have isOnMission set correctly)
      if (!pendingOrder._splitKey && isOrderOnMission(pendingOrder.id)) {
        alert(t('blackOps.cannotAssignOnMission', { name: pendingOrder.name }));
        setPendingOrder(null);
        return;
      }

      // Check if user has enough copies available
      if (!hasEnoughCopies(pendingOrder)) {
        const ownedCount = pendingOrder.ownedCount !== undefined ? pendingOrder.ownedCount : 0;
        const alreadySelected = countOrderInSelection(pendingOrder.id);
        const isAre = alreadySelected === 1 ? t('blackOps.is') : t('blackOps.are');
        alert(t('blackOps.cannotAssignCopies', { name: pendingOrder.name, owned: ownedCount, selected: alreadySelected, isAre }));
        setPendingOrder(null);
        return;
      }
      
      const newSelectedOrders = [...selectedOrders];
      newSelectedOrders[index] = pendingOrder;
      setSelectedOrders(newSelectedOrders);
      setPendingOrder(null);
      setSelectedBoxIndex(null);
    } else {
      // Otherwise, toggle box selection for future order placement
      setSelectedBoxIndex(selectedBoxIndex === index ? null : index);
    }
  };

  // Calculate available intel (for display - shows actual available intel)
  const calculateAvailableIntel = () => {
    const usedIntel = selectedOrders.reduce((sum, order) => {
      return sum + (order ? (order.cost || 0) : 0);
    }, 0);
    return currentIntel - usedIntel;
  };
  
  // Calculate available intel for mission spending (respects maxIntelPerMission limit)
  const calculateAvailableIntelForMission = () => {
    const usedIntel = selectedOrders.reduce((sum, order) => {
      return sum + (order ? (order.cost || 0) : 0);
    }, 0);
    const available = currentIntel - usedIntel;
    
    // If maxIntelPerMission is set (> 0), limit available intel to that amount
    if (maxIntelPerMission > 0) {
      const remainingForMission = maxIntelPerMission - usedIntel;
      return Math.min(available, remainingForMission);
    }
    
    return available;
  };

  const availableIntel = calculateAvailableIntel(); // For display - shows actual available
  const availableIntelForMission = calculateAvailableIntelForMission(); // For validation - respects mission limit

  // Check if an order can be afforded (respects maxIntelPerMission limit)
  const canAffordOrder = (order) => {
    const orderCost = order.cost || 0;
    return orderCost <= availableIntelForMission;
  };

  // Count how many times an order is already selected
  const countOrderInSelection = (orderId) => {
    return selectedOrders.filter(order => order && order.id === orderId).length;
  };

  // Check if an order is currently on an active mission
  const isOrderOnMission = (orderId) => {
    return missionBoxes.some(box => 
      box.active && 
      box.operatives && 
      box.operatives.includes(orderId)
    );
  };

  // Check if user has enough copies of an order available
  const hasEnoughCopies = (order) => {
    const ownedCount = order.ownedCount !== undefined ? order.ownedCount : (order.isOwned ? 999 : 0);
    // For 'Created' orders, assume unlimited
    if (order.availability === 'Created') {
      return true;
    }
    const alreadySelected = countOrderInSelection(order.id);
    return alreadySelected < ownedCount;
  };

  // Check if order can be assigned (has copies AND not on active mission AND not wounded)
  const canAssignOrder = (order) => {
    // Check if order is wounded
    if (order.isWounded) {
      return false;
    }
    // Check if order is on an active mission
    // If order has been split (has _splitKey), only check order.isOnMission
    // Otherwise, also check missionBoxes for backward compatibility
    if (order.isOnMission) {
      return false;
    }
    // Only check missionBoxes if this order hasn't been split
    // (split orders already have isOnMission set correctly)
    if (!order._splitKey && isOrderOnMission(order.id)) {
      return false;
    }
    // Check if user has enough copies
    return hasEnoughCopies(order);
  };
  
  const getWoundedAvailableAt = (orderLike) => {
    const tryExtract = (candidate) => {
      if (!candidate || typeof candidate !== 'object') return null;
      const direct =
        candidate.availableAt ||
        candidate.available_at ||
        candidate.woundedAvailableAt ||
        candidate.wounded_available_at ||
        null;
      if (direct) return direct;

      // Fallback: inspect any key containing "available" and return parseable date-like value.
      for (const [key, value] of Object.entries(candidate)) {
        if (!/available/i.test(String(key))) continue;
        if (!value) continue;
        const dt = new Date(value);
        if (!Number.isNaN(dt.getTime())) {
          return value;
        }
      }
      return null;
    };

    const direct = tryExtract(orderLike);
    if (direct) return direct;

    const fallback = (availableOrders || []).find((o) => {
      const sameId = Number(o?.id) === Number(orderLike?.id);
      const isWounded = o?.isWounded === true || (Number(o?.woundedCount) || 0) > 0;
      const hasTime = tryExtract(o);
      return sameId && isWounded && hasTime;
    });
    return tryExtract(fallback);
  };

  // Format remaining wounded time (supports snake_case and camelCase API fields).
  const formatWoundedTime = (orderLike) => {
    const availableAt = getWoundedAvailableAt(orderLike);
    if (!availableAt) return '--:--';
    const available = new Date(availableAt);
    const now = new Date();
    const remainingMs = available.getTime() - now.getTime();
    
    if (remainingMs <= 0) return t('blackOps.availableNow');
    
    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Find remaining mission time for an operative currently deployed.
  const getOperativeMissionRemainingMs = (orderId) => {
    if (orderId == null) return null;
    const activeMission =
      [...(missionBoxes || []), ...(supplyRunMissions || [])].find(
        (box) =>
          box?.active &&
          Array.isArray(box?.operatives) &&
          box.operatives.includes(orderId) &&
          box.timer != null
      ) || null;

    if (!activeMission) return null;
    return Number.isFinite(activeMission.timer) ? activeMission.timer : null;
  };

  const formatDeployedTime = (orderId) => {
    const remainingMs = getOperativeMissionRemainingMs(orderId);
    if (remainingMs == null) return '--:--:--';
    if (remainingMs <= 0) return t('blackOps.availableNow');
    return formatTimer(remainingMs);
  };

  const handleOrderSelect = (order) => {
    // Check if order is wounded
    if (order.isWounded) {
      const remainingTime = formatWoundedTime(order);
      alert(t('blackOps.cannotSelectWounded', { name: order.name, time: remainingTime }));
      return;
    }
    
    // Check if order can be afforded
    if (!canAffordOrder(order)) {
      alert(t('blackOps.cannotAfford', { name: order.name, cost: order.cost, available: availableIntelForMission }));
      return;
    }

    // Check if order is on an active mission
    // If order has been split (has _splitKey), only check order.isOnMission
    // Otherwise, also check missionBoxes for backward compatibility
    if (order.isOnMission) {
      alert(t('blackOps.cannotAssignOnMission', { name: order.name }));
      return;
    }
    // Only check missionBoxes if this order hasn't been split
    // (split orders already have isOnMission set correctly)
    if (!order._splitKey && isOrderOnMission(order.id)) {
      alert(t('blackOps.cannotAssignOnMission', { name: order.name }));
      return;
    }

    // Check if user has enough copies available
    if (!hasEnoughCopies(order)) {
      const ownedCount = order.ownedCount !== undefined ? order.ownedCount : 0;
      const alreadySelected = countOrderInSelection(order.id);
      const isAre = alreadySelected === 1 ? t('blackOps.is') : t('blackOps.are');
      alert(t('blackOps.cannotAssignCopies', { name: order.name, owned: ownedCount, selected: alreadySelected, isAre }));
      return;
    }

    // On mobile, automatically assign to next free slot
    const isMobile = window.innerWidth <= 1024;
    if (isMobile) {
      // Find the first empty slot
      const firstEmptyIndex = selectedOrders.findIndex(slot => slot === null);
      if (firstEmptyIndex !== -1) {
        const newSelectedOrders = [...selectedOrders];
        newSelectedOrders[firstEmptyIndex] = order;
        setSelectedOrders(newSelectedOrders);
        setSelectedBoxIndex(null);
        setPendingOrder(null);
        return;
      } else {
        // All slots are full
        alert(t('blackOps.allSlotsFull'));
        return;
      }
    }

    // Desktop behavior: If a box is already selected, place the order there
    if (selectedBoxIndex !== null) {
      const newSelectedOrders = [...selectedOrders];
      newSelectedOrders[selectedBoxIndex] = order;
      setSelectedOrders(newSelectedOrders);
      setSelectedBoxIndex(null);
      setPendingOrder(null);
    } else {
      // Otherwise, set as pending order (all boxes will become active)
      setPendingOrder(order);
    }
  };

  const handleResetAssignment = () => {
    setSelectedOrders([null, null, null]);
    setSelectedBoxIndex(null);
    setPendingOrder(null);
  };

  const formatTimer = (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return '00:00:00';
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const handleCompleteMission = async (boxId) => {
    // Prevent double-clicks - if this box is already being completed, return early
    if (completingBoxIdsRef.current.has(boxId)) {
      console.log(`[BlackOps] Box ${boxId} is already being completed, ignoring duplicate click`);
      return;
    }

    // Check both regular missions and Supply Run missions
    // Backend is the authority - only check if box and missionId exist, let backend decide if mission can be completed
    const box = missionBoxes.find(b => b.id === boxId) || supplyRunMissions.find(b => b.id === boxId);
    if (!box || !box.missionId) {
      alert(t('blackOps.missionNotCompleted'));
      return;
    }

    // Store the missionId we're completing to verify the response matches
    const completingMissionId = box.missionId;
    
    // Mark this box as being completed to prevent double-clicks
    completingBoxIdsRef.current.add(boxId);
    
    // Add this mission ID to the completed set to prevent it from reappearing
    if (completingMissionId) {
      completedMissionIdsRef.current.add(completingMissionId);
      // Clear old entries after 10 seconds (missions should be deleted from DB by then)
      setTimeout(() => {
        completedMissionIdsRef.current.delete(completingMissionId);
      }, 10000);
    }

    try {
      const res = await fetch(blackOpsUrlWithLang('/blackops/complete-mission'), {
        method: 'POST',
        headers: {
          ...blackOpsAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          missionId: completingMissionId,
          devBypassTimeCheck: process.env.NODE_ENV === 'development' && isPlayer1
        }),
      });

      const data = await res.json();
      
      if (data.error) {
        alert(t('blackOps.failedToCompleteMission', { error: data.error }));
        completingBoxIdsRef.current.delete(boxId);
        return;
      }

      if (data.achievementUnlocked) {
        setAchievementUnlocked(data.achievementUnlocked);
      }

      // Get mission summary from response (outcome, location, wounded units)
      // Verify that the missionId in the response matches what we sent
      if (data.missionSummary) {
        const summary = data.missionSummary;
        
        // Verify missionId matches to prevent showing wrong mission summary
        if (summary.missionId && summary.missionId !== completingMissionId) {
          console.error('Mission ID mismatch! Sent:', completingMissionId, 'Received:', summary.missionId);
          // Don't show summary if IDs don't match - this prevents showing wrong mission
          alert(t('blackOps.missionDataMismatch'));
          completingBoxIdsRef.current.delete(boxId);
          return;
        }
        
        if (summary.outcome && summary.location_name) {
          let outcomeText = summary.outcome === 'success' 
            ? t('blackOps.missionSuccessful', { location: summary.location_name })
            : t('blackOps.missionFailure', { location: summary.location_name });
          
          // Add PvP header if opponent player name exists
          if (summary.opponent_player_name) {
            const pvpHeader = `You encountered a team of operatives on this mission! Mission lead ${summary.opponent_player_name}\n\n`;
            outcomeText = pvpHeader + outcomeText;
          }
          
          setMissionOutcomeMessage({
            text: outcomeText,
            outcome: summary.outcome,
            isPvP: !!summary.opponent_player_name
          });
          
          // Fetch wounded unit names if wounded_units exist
          if (summary.wounded_units && Array.isArray(summary.wounded_units) && summary.wounded_units.length > 0) {
            try {
              // Get current language from i18n and normalize it for the API
              const currentLanguage = i18nInstance.language || 'en-us';
              const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
              
              // Fetch all orders once and map wounded unit IDs to names
              const orderRes = await fetch(`${API_URL}/all-orders?lang=${encodeURIComponent(normalizedLanguage)}`, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
              });
              const allOrders = await orderRes.json();
              const woundedUnitNames = summary.wounded_units
                .map(unitId => {
                  const woundedOrder = allOrders.find(o => o.id === unitId);
                  return woundedOrder ? woundedOrder.name : null;
                })
                .filter(name => name !== null);
              setWoundedUnits(woundedUnitNames);
            } catch (err) {
              console.error('Failed to fetch wounded unit names:', err);
              setWoundedUnits([]);
            }
          } else {
            setWoundedUnits([]);
          }
        }
      }

      // Check for rewards and show modal
      if ((data.rewards && Array.isArray(data.rewards) && data.rewards.length > 0) || (data.intel && data.intel > 0) || data.resource || data.building || data.billyBucks || data.flashlight) {
        const hasLegendary = data.rewards && data.rewards.some(r => r.rarity && r.rarity.toLowerCase() === 'legendary');
        const rewards = data.rewards || [];
        setMissionRewards(rewards);
        setHasLegendaryReward(hasLegendary || false);
        setIntelReward(data.intel || 0);
        setResourceReward(data.resource || null);
        setBuildingReward(data.building || null);
        setBillyBucksReward(data.billyBucks || null);
        setFlashlightReward(data.flashlight || null);
        // Initialize revealedRewards array - one for each reward plus intel, resource, building, billyBucks if present
        const totalRewards = rewards.length + (data.intel && data.intel > 0 ? 1 : 0) + (data.resource ? 1 : 0) + (data.building ? 1 : 0) + (data.billyBucks ? 1 : 0) + (data.flashlight ? 1 : 0);
        setRevealedRewards(new Array(totalRewards).fill(false));
        if (totalRewards > 0) {
          playSoundEffect('/sounds/hidden_rewards.mp3');
        }
        setRewardModalOpen(true);
      }

      // Immediately clear the completed mission from UI to hide the complete button
      // Don't set the new mission ID yet - let fetchLocations() populate it after refresh
      const isSupplyRunBox = boxId >= 100;
      
      // Always clear the mission immediately to hide the complete button
      if (isSupplyRunBox) {
        // Clear Supply Run mission
        setSupplyRunMissions(prev => prev.map(b => 
          b.id === boxId 
            ? { 
                ...b, 
                active: false,
                completed: false,
                timer: null,
                timerStart: null,
                outcome: null,
                operatives: [],
                missionId: null, // Clear missionId immediately to hide complete button
                location: '', // Clear location to show loading state
                difficulty: '', // Clear difficulty
                _justCompleted: true // Flag to prevent fetchActiveMissions from overwriting
              }
            : b
        ));
      } else {
        // Clear regular mission
        setMissionBoxes(prev => prev.map(b => 
          b.id === boxId 
            ? { 
                ...b, 
                active: false,
                completed: false,
                timer: null,
                timerStart: null,
                outcome: null,
                operatives: [],
                missionId: null, // Clear missionId immediately to hide complete button
                location: '', // Clear location to show loading state
                difficulty: '', // Clear difficulty
                _justCompleted: true // Flag to prevent fetchActiveMissions from overwriting
              }
            : b
        ));
      }
      
      // Refresh orders list to update wounded status
      await fetchOrders();

      // Refresh intel to update activeMissionCount (one mission completed = one slot freed)
      await fetchIntel();
      
      // Fetch locations to get pre-generated missions (this will update boxes with new missions)
      // Don't call fetchActiveMissions here - it can cause race conditions where completed
      // missions reappear. The state update above already handles the completed mission removal,
      // and fetchLocations will populate the new pre-generated mission.
      await fetchLocations();
      
      // Clear the _justCompleted flag after fetchLocations completes to allow state to settle
      // This ensures that boxes with _justCompleted are protected during the entire update process
      setTimeout(() => {
        setMissionBoxes(prev => prev.map(b => 
          b._justCompleted 
            ? { ...b, _justCompleted: false }
            : b
        ));
        setSupplyRunMissions(prev => prev.map(b => 
          b._justCompleted 
            ? { ...b, _justCompleted: false }
            : b
        ));
        
        // After clearing the flag, refresh missions to ensure boxes get updated with new missions
        // This fixes the issue where completed missions don't refresh until rejoining the menu
        fetchLocations().catch(err => {
          console.error('Error refreshing missions after completion:', err);
        });
        
        // Remove from completing set after a delay to allow state to settle
        setTimeout(() => {
          completingBoxIdsRef.current.delete(boxId);
        }, 1000);
      }, 500); // Increased timeout to ensure all async operations complete
    } catch (err) {
      console.error('Error completing mission:', err);
      alert(t('blackOps.failedToCompleteMission', { error: err.message }));
      // Remove from completing set on error
      completingBoxIdsRef.current.delete(boxId);
    }
  };

  const handleSendMission = async (boxId) => {
    // Check if all 3 boxes have operatives
    if (selectedOrders.some(order => !order)) {
      alert(t('blackOps.select3Operatives'));
      return;
    }

    // Check max active missions (backend enforces this; frontend prevents unnecessary requests)
    if (activeMissionCount >= maxActiveMissions) {
      alert(t('blackOps.maxMissionsReached', { max: maxActiveMissions }));
      return;
    }

    // Check both regular missions and Supply Run missions
    const box = missionBoxes.find(b => b.id === boxId) || supplyRunMissions.find(b => b.id === boxId);
    if (box && box.active) {
      alert(t('blackOps.missionInProgress'));
      return;
    }

    // Check if mission is completed (should use Complete button instead)
    if (box && box.completed) {
      alert(t('blackOps.missionCompletedUseButton'));
      return;
    }

    try {
      const operatives = selectedOrders.map(order => order.id);
      // Use configured mission length
      const missionLengthMs = missionLengthSeconds * 1000; // Convert to milliseconds
      
      if (!box?.missionId) {
        alert(t('blackOps.missionNotFound'));
        return;
      }
      
      const res = await fetch(blackOpsUrlWithLang('/blackops/mission'), {
        method: 'POST',
        headers: {
          ...blackOpsAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          operatives,
          missionId: box.missionId,
          missionLength: missionLengthSeconds
        }),
      });

      const data = await res.json();
      
      if (data.error) {
        if (data.error === 'MAX_ACTIVE_MISSIONS_REACHED') {
          alert(t('blackOps.maxMissionsReached', { max: data.maxActiveMissions ?? maxActiveMissions }));
        } else {
          alert(t('blackOps.missionFailed', { error: data.message || data.error }));
        }
        return;
      }

      // Start timer for this box (mission resolves instantly but result comes later)
      if (data.missionId) {
        // Use database-calculated completionTime from response (now calculated by database)
        const completionTime = new Date(data.completionTime);
        const now = new Date();
        const actualRemaining = Math.max(0, completionTime.getTime() - now.getTime());
        
        console.log(`Mission sent! Box ${boxId}, missionId: ${data.missionId}, remaining: ${actualRemaining}ms`);
        console.log(`  Completion time: ${completionTime.toISOString()}, Current: ${now.toISOString()}`);
        
        // Update the appropriate mission box (regular or Supply Run)
        if (boxId < 100) {
          // Regular mission
          setMissionBoxes(prev => prev.map(b => 
            b.id === boxId 
              ? { 
                  ...b, 
                  timer: actualRemaining, 
                  timerStart: new Date(data.sendTime).getTime(), 
                  active: true,
                  completed: false,
                  missionId: data.missionId,
                  operatives: operatives // Store the operative IDs on this mission
                }
              : b
          ));
        } else {
          // Supply Run mission
          setSupplyRunMissions(prev => prev.map(b => 
            b.id === boxId 
              ? { 
                  ...b, 
                  timer: actualRemaining, 
                  timerStart: new Date(data.sendTime).getTime(), 
                  active: true,
                  completed: false,
                  missionId: data.missionId,
                  operatives: operatives // Store the operative IDs on this mission
                }
              : b
          ));
        }
        
        // Immediately fetch active missions to sync with database completion time
        // This ensures timer matches database even if there's any timing difference
        setTimeout(() => {
          fetchActiveMissions();
        }, 1000);
        
        // Clear selected orders after mission is sent
        setSelectedOrders([null, null, null]);
        setPendingOrder(null);
        setSelectedBoxIndex(null);
        
                  // Refresh intel after mission is sent (cost was deducted)
        const intelRes = await fetch(`${API_URL}/blackops/intel`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const intelData = await intelRes.json();
        if (intelData.currentIntel !== undefined) {
          setCurrentIntel(intelData.currentIntel);
        }
        if (intelData.activeMissionCount !== undefined) {
          setActiveMissionCount(intelData.activeMissionCount);
        }
        if (intelData.maxActiveMissions !== undefined) {
          setMaxActiveMissions(intelData.maxActiveMissions);
        }
        
        // Reset selection after mission is sent (same as Reset Assignment button)
        setSelectedOrders([null, null, null]);
        setSelectedBoxIndex(null);
        setPendingOrder(null);
        
        // Refresh orders list to update operatives status (show as "on mission")
        await fetchOrders();
      }
      
      // Don't show result yet - mission resolves instantly but result comes after timer
      // Result will be fetched when timer completes or on page refresh
    } catch (err) {
      console.error('Error sending mission:', err);
      alert(t('blackOps.failedToSendMission', { error: err.message }));
    }
  };

  // Dev function: Instantly complete all ongoing missions (PLAYER 1 ONLY)
  const handleDevCompleteMissions = async () => {
    if (!isPlayer1) {
      alert('This feature is only available for player1');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/blackops/dev-complete-missions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await res.json();
      
      if (data.error) {
        alert(`Failed to complete missions: ${data.error}`);
        return;
      }

      // Refresh active missions to update UI
      await fetchActiveMissions();
      await fetchOrders();
      
      alert(`Instantly completed ${data.completedCount || 0} mission(s)`);
    } catch (err) {
      console.error('Error completing missions:', err);
      alert(`Failed to complete missions: ${err.message}`);
    }
  };

  if (isExiting || location.pathname !== '/blackops') {
    return null;
  }

  return (
    <div className="blackops-viewport-wrapper">
      {achievementUnlocked && (
        <AchievementUnlockedToast
          titleId={achievementUnlocked.titleId}
          titleName={achievementUnlocked.titleName}
          color={achievementUnlocked.color}
          achievementKey={achievementUnlocked.achievementKey}
          onClose={() => setAchievementUnlocked(null)}
        />
      )}
      {pendingToast && (
        <AchievementUnlockedToast
          titleId={pendingToast.titleId}
          titleName={pendingToast.titleName}
          color={pendingToast.color}
          achievementKey={pendingToast.achievementKey}
          onClose={dismissPendingToast}
        />
      )}
      <div
        className="blackops-fullscreen-background"
        style={{ backgroundImage: 'url(/images/blackops_bg.jpg)' }}
      />
      <div className="blackops-container">
      <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10001, display: 'flex', alignItems: 'center' }}>
        <FriendsIcon />
        <SettingsMenu onLogout={() => {
          onLogout();
          navigate('/');
        }} />
        <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
      </div>

      {/* Intel Counter - Top Middle */}
      <div className="intel-counter-top">
        <span className="intel-label">{t('blackOps.availableIntel')}</span>
        <span className={`intel-value ${availableIntel < 0 ? 'intel-negative' : ''}`}>
          {availableIntel} / {maxIntel}
        </span>
      </div>

      <MenuBackButton onClick={exitToBattleOverview} />

      {/* Dev Button: Instantly Complete Missions (PLAYER 1 ONLY) */}
      {process.env.NODE_ENV === 'development' && isPlayer1 && (
        <button
          className="dev-complete-missions-button"
          onClick={handleDevCompleteMissions}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: '#4169e1',
            color: '#fff',
            border: '2px solid #ffa500',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 'bold',
            borderRadius: '8px',
            cursor: 'pointer',
            zIndex: 1000,
            boxShadow: '0 4px 15px rgba(65, 105, 225, 0.4)',
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#5179f1';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#4169e1';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          ⚡ Instantly Complete Missions (DEV)
        </button>
      )}

      {/* Mission Panel - Right Side (Same structure as operatives panel) */}
      <div className="mission-panel">
        <div className="mission-panel-header">
          <h3>{t('blackOps.missions')}</h3>
        </div>
        <div className="mission-boxes-container">
          {/* Combine regular missions and Supply Run missions */}
          {[...missionBoxes, ...supplyRunMissions].map((box) => {
          const rawDifficulty = (box.difficulty && String(box.difficulty).trim()) ? String(box.difficulty).trim() : 'Risky';
          const difficultyLower = rawDifficulty.toLowerCase();
          
          // Check if this is a Supply Run mission and use specific image (parse canonical English title)
          let backgroundImage;
          const supplyRunCanonical =
            typeof box.locationNameEn === 'string' && box.locationNameEn
              ? box.locationNameEn
              : box.isSupplyRun === true &&
                  typeof box.location === 'string' &&
                  box.location.startsWith('Supply run (')
                ? box.location
                : '';
          if (box.isSupplyRun === true || supplyRunCanonical.startsWith('Supply run (')) {
            const resourceMatch = supplyRunCanonical.match(/Supply run \((.+)\)/);
            if (resourceMatch) {
              const resourceName = resourceMatch[1].toLowerCase();
              // Map resource names to specific images
              const supplyRunImages = {
                'biomass': '/images/blackops_biomass.jpg',
                'erray': '/images/blackops_erray.jpg',
                'food rations': '/images/blackops_cattle.jpg',
                'lavender': '/images/blackops_lavender.jpg',
                'stone': '/images/blackops_stone.jpg'
              };
              backgroundImage = supplyRunImages[resourceName] || '/images/bo_dangerous.jpg';
            } else {
              backgroundImage = '/images/bo_dangerous.jpg';
            }
          } else {
            // Regular missions use difficulty-based images
            backgroundImage = difficultyLower === 'dangerous' 
              ? '/images/bo_dangerous.jpg'
              : difficultyLower === 'risky'
              ? '/images/bo_risky.jpg'
              : '/images/bo_easy.jpg';
          }
          
          return (
            <div 
              key={box.isSupplyRun ? `supply-${box.missionId || box.id}` : `regular-${box.id}`} 
              className={`mission-box difficulty-${difficultyLower}`}
              style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              <div className="mission-box-location">{box.location || t('blackOps.loading')}</div>
              <div className={`mission-box-difficulty difficulty-${difficultyLower}`}>
                {translateBlackOpsDifficulty(t, rawDifficulty)}
              </div>
              <div className="mission-box-timer">
                {box.completed 
                  ? '00:00:00' 
                  : (box.active 
                      ? (box.timer !== null && box.timer !== undefined 
                          ? formatTimer(box.timer) 
                          : '00:00:00')
                      : formatTimer(box.missionLength * 1000))}
              </div>
              {box.active && box.missionId && (box.completed || (box.timer !== null && box.timer <= 0)) ? (
                <MenuPaintedButton
                  className="mission-box-complete-button"
                  imagePath={BLACK_OPS_COMPLETE_BUTTON_PATH}
                  surfaceFilter={BLACK_OPS_COMPLETE_BUTTON_SURFACE_FILTER}
                  onClick={() => handleCompleteMission(box.id)}
                >
                  {t('blackOps.complete')}
                </MenuPaintedButton>
              ) : box.active ? null : (
                <MenuPaintedButton
                  className="mission-box-send-button"
                  imagePath={BLACK_OPS_SEND_BUTTON_PATH}
                  onClick={() => handleSendMission(box.id)}
                  disabled={selectedOrders.some(order => !order) || activeMissionCount >= maxActiveMissions}
                  title={activeMissionCount >= maxActiveMissions ? t('blackOps.maxMissionsReached', { max: maxActiveMissions }) : undefined}
                >
                  {t('blackOps.send')}
                </MenuPaintedButton>
              )}
            </div>
          );
        })}
        </div>
      </div>

      {/* Order Selector Panel */}
      <div className={`order-selector-panel${layout ? ' order-selector-panel--responsive' : ''}`}>
        <div className="order-selector-header">
          <h3>{t('blackOps.operatives')}</h3>
          <MenuPaintedButton
            className="reset-assignment-button"
            imagePath={BLACK_OPS_RESET_BUTTON_PATH}
            onClick={handleResetAssignment}
          >
            {t('blackOps.resetAssignment')}
          </MenuPaintedButton>
        </div>
        <div className="order-selector-search">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder={t('blackOps.searchOperatives')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="order-search-input"
            />
            {searchTerm && (
              <button
                className="search-clear-button"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
          <div className="order-selector-list">
            {loading ? (
              <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>
                {t('blackOps.loadingOrders')}
              </div>
            ) : availableOrders.length === 0 ? (
              <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>
                {t('blackOps.noOrdersAvailable')}
              </div>
            ) : (() => {
              const filteredOrders = availableOrders.filter(order =>
                order.name.toLowerCase().includes(searchTerm.toLowerCase())
              );
              return filteredOrders.length === 0 ? (
                <div style={{ color: 'white', textAlign: 'center', padding: '20px' }}>
                  {t('blackOps.noOperativesFound')} "{searchTerm}"
                </div>
              ) : (
                <OrderSidebarOrderList
                  items={filteredOrders}
                  getGroupKey={blackOpsOperativeGroupKey}
                  getGroupCount={(group) => {
                    const baseCount = blackOpsOperativeGroupCount(group);
                    const first = group?.items?.[0] ?? group?.item;
                    const orderId = first?.id ?? first?.order_id;
                    if (orderId == null) {
                      return baseCount;
                    }

                    // Available pool should shrink as operatives are assigned to active duty.
                    const isAvailableGroup =
                      first?._splitKey
                        ? String(first._splitKey).endsWith('_available')
                        : !first?.isWounded && !first?.isOnMission;
                    if (!isAvailableGroup) {
                      return baseCount;
                    }

                    const selectedCount = countOrderInSelection(orderId);
                    return Math.max(0, baseCount - selectedCount);
                  }}
                  getDisplayCost={(order) => order.cost ?? order.intel_cost ?? 0}
                  costLabel={layout ? t('blackOps.intelRewardLabel') : t('strategy.cost')}
                  listClassName={layout ? 'blackops-operatives-list--responsive' : ''}
                  isItemDisabled={(order, group) => {
                    const pick =
                      group.items.find((o) => canAffordOrder(o) && canAssignOrder(o)) || order;
                    return !(canAffordOrder(pick) && canAssignOrder(pick));
                  }}
                  isItemUnowned={(order, group) => {
                    const o = group.items[0];
                    const baseCount = blackOpsOperativeGroupCount(group);
                    const orderId = o?.id ?? o?.order_id;
                    const isAvailableGroup =
                      o?._splitKey
                        ? String(o._splitKey).endsWith('_available')
                        : !o?.isWounded && !o?.isOnMission;
                    const remainingCount =
                      isAvailableGroup && orderId != null
                        ? Math.max(0, baseCount - countOrderInSelection(orderId))
                        : baseCount;

                    // When count reaches 0 because copies are assigned, keep only gray-out (no warning triangle).
                    if (remainingCount === 0) {
                      return false;
                    }

                    const canSelect =
                      group.items.some((copy) => canAffordOrder(copy) && canAssignOrder(copy));
                    return !canSelect && !o.isWounded && !o.isOnMission;
                  }}
                  onItemClick={(order, group) => {
                    const pick =
                      group.items.find((o) => canAffordOrder(o) && canAssignOrder(o)) || order;
                    if (canAffordOrder(pick) && canAssignOrder(pick)) {
                      handleOrderSelect(pick);
                    }
                  }}
                  onItemMouseEnter={(order, group, e) => {
                    if (window.innerWidth > 1024) {
                      setHoveredOrder(group.items[0]);
                    }
                  }}
                  onItemMouseLeave={() => {
                    if (window.innerWidth > 1024) {
                      setHoveredOrder(null);
                    }
                  }}
                  renderRowWrapperClassName={(order, group) =>
                    {
                      const canSelect =
                        group.items.some((copy) => canAffordOrder(copy) && canAssignOrder(copy));
                      return [
                        !canSelect ? 'is-disabled' : '',
                        order.isWounded ? 'has-wounded-indicator' : '',
                        order.isOnMission ? 'has-mission-indicator' : '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                    }
                  }
                  renderAfterCard={(order) => (
                    <>
                      {order.isWounded && (
                        <span className="wounded-indicator" aria-label="Wounded">
                          <img src="/images/drop.png" alt="Wounded" />
                        </span>
                      )}
                      {order.isOnMission && (
                        <span className="mission-indicator" aria-label="On Mission" />
                      )}
                    </>
                  )}
                />
              );
            })()}
          </div>

        {/* Save/Load Composition Buttons */}
        <div className="composition-buttons-container">
        <MenuPaintedButton
          className="save-composition-button"
          imagePath={BLACK_OPS_COMPOSITION_BUTTON_PATH}
          onClick={() => {
            if (!selectedOrders[0] || !selectedOrders[1] || !selectedOrders[2]) {
              alert(t('blackOps.select3Operatives'));
              return;
            }
            setSaveModalOpen(true);
          }}
          disabled={!selectedOrders[0] || !selectedOrders[1] || !selectedOrders[2]}
        >
          {t('blackOps.save')}
        </MenuPaintedButton>
        <MenuPaintedButton
          className="load-composition-button"
          imagePath={BLACK_OPS_COMPOSITION_BUTTON_PATH}
          onClick={() => setLoadModalOpen(true)}
        >
          {t('blackOps.load')}
        </MenuPaintedButton>
        </div>
      </div>

      {/* Save Composition Modal */}
      {saveModalOpen && (
        <div className="composition-modal-overlay" onClick={() => {
          setSaveModalOpen(false);
          setCompositionName('');
        }}>
          <div className="composition-modal" onClick={(e) => e.stopPropagation()}>
            <div className="composition-modal-header">
              <h3>{t('blackOps.saveComposition')}</h3>
              <button
                className="composition-modal-close"
                onClick={() => {
                  setSaveModalOpen(false);
                  setCompositionName('');
                }}
              >
                ×
              </button>
            </div>
            <div className="composition-modal-content">
              <label>{t('blackOps.compositionName')}</label>
              <input
                type="text"
                value={compositionName}
                onChange={(e) => setCompositionName(e.target.value)}
                placeholder={t('blackOps.compositionNamePlaceholder')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveComposition();
                  }
                }}
                autoFocus
              />
              <div className="composition-modal-actions">
                <button
                  className="composition-modal-save"
                  onClick={handleSaveComposition}
                  disabled={!compositionName.trim()}
                >
                  {t('blackOps.save')}
                </button>
                <button
                  className="composition-modal-cancel"
                  onClick={() => {
                    setSaveModalOpen(false);
                    setCompositionName('');
                  }}
                >
                  {t('blackOps.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Load Composition Modal */}
      {loadModalOpen && (
        <div className="composition-modal-overlay" onClick={() => setLoadModalOpen(false)}>
          <div className="composition-modal" onClick={(e) => e.stopPropagation()}>
            <div className="composition-modal-header">
              <h3>{t('blackOps.loadComposition')}</h3>
              <button
                className="composition-modal-close"
                onClick={() => setLoadModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="composition-modal-content">
              {savedCompositions.length === 0 ? (
                <div className="composition-empty-state">
                  {t('blackOps.noCompositionsSaved')}
                </div>
              ) : (
                <div className="composition-list">
                  {savedCompositions.map((composition) => {
                    const findAvailableOrder = (orderId) => {
                      return availableOrders.find(o => 
                        o.id === orderId && 
                        !o.isWounded && 
                        !o.isOnMission &&
                        (o.ownedCount > 0 || o.availability === 'Created')
                      );
                    };
                    
                    const order1 = availableOrders.find(o => o.id === composition.operative1_id);
                    const order2 = availableOrders.find(o => o.id === composition.operative2_id);
                    const order3 = availableOrders.find(o => o.id === composition.operative3_id);
                    const availableOrder1 = findAvailableOrder(composition.operative1_id);
                    const availableOrder2 = findAvailableOrder(composition.operative2_id);
                    const availableOrder3 = findAvailableOrder(composition.operative3_id);
                    const allAvailable = availableOrder1 && availableOrder2 && availableOrder3;
                    
                    return (
                      <div
                        key={composition.id}
                        className={`composition-item ${!allAvailable ? 'composition-unavailable' : ''}`}
                        onClick={() => allAvailable && handleLoadComposition(composition)}
                      >
                        <div className="composition-item-header">
                          <span className="composition-item-name">{composition.name}</span>
                          <button
                            className="composition-item-delete"
                            onClick={(e) => handleDeleteComposition(composition.id, e)}
                            title={t('blackOps.deleteComposition')}
                          >
                            ×
                          </button>
                        </div>
                        <div className="composition-item-operatives">
                          <div className={`composition-operative-image ${availableOrder1 ? '' : 'unavailable'}`}>
                            {order1 ? (
                              <OrderDesign
                                order={order1}
                                minimal={true}
                                noMargin={true}
                                gameState={null}
                                scale={0.15}
                              />
                            ) : (
                              <div className="composition-operative-placeholder">
                                ID: {composition.operative1_id}
                              </div>
                            )}
                          </div>
                          <div className={`composition-operative-image ${availableOrder2 ? '' : 'unavailable'}`}>
                            {order2 ? (
                              <OrderDesign
                                order={order2}
                                minimal={true}
                                noMargin={true}
                                gameState={null}
                                scale={0.15}
                              />
                            ) : (
                              <div className="composition-operative-placeholder">
                                ID: {composition.operative2_id}
                              </div>
                            )}
                          </div>
                          <div className={`composition-operative-image ${availableOrder3 ? '' : 'unavailable'}`}>
                            {order3 ? (
                              <OrderDesign
                                order={order3}
                                minimal={true}
                                noMargin={true}
                                gameState={null}
                                scale={0.15}
                              />
                            ) : (
                              <div className="composition-operative-placeholder">
                                ID: {composition.operative3_id}
                              </div>
                            )}
                          </div>
                        </div>
                        {!allAvailable && (
                          <div className="composition-item-warning">
                            {t('blackOps.compositionNotAvailable')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Tooltip - hidden on mobile via CSS */}
      {hoveredOrder && (
        <div className="order-tooltip">
          <OrderDesign
            order={hoveredOrder}
            minimal={false}
            noMargin={true}
            gameState={null}
            scale={0.8}
          />
          {hoveredOrder.isWounded && (
            <div className="wounded-tooltip-info">
              <div className="wounded-tooltip-label">{t('blackOps.wounded')}</div>
              <div className="wounded-tooltip-time">
                {t('blackOps.availableIn')} {formatWoundedTime(hoveredOrder)}
              </div>
            </div>
          )}
          {hoveredOrder.isOnMission && (
            <div className="wounded-tooltip-info deployed-tooltip-info">
              <div className="wounded-tooltip-label">{t('blackOps.deployed')}</div>
              <div className="wounded-tooltip-time">
                {t('blackOps.backIn')} {formatDeployedTime(hoveredOrder.id)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reward Modal */}
      {rewardModalOpen && (
        <div className="reward-modal-overlay">
          <div 
            className={`reward-modal ${hasLegendaryReward ? 'legendary-reward' : ''}`}
            style={{
              maxWidth: (missionRewards.length + (intelReward > 0 ? 1 : 0) + (resourceReward ? 1 : 0) + (buildingReward ? 1 : 0) + (billyBucksReward ? 1 : 0) + (flashlightReward ? 1 : 0)) > 3 ? '1800px' : '1350px'
            }}
          >
            <div className="reward-modal-header">
              <h2>{t('blackOps.missionRewards')}</h2>
              {hasLegendaryReward && (
                <div className="legendary-success-text">{t('blackOps.legendarySuccess')}</div>
              )}
            </div>
            <div className="reward-modal-content">
              <div className="reward-orders-display">
                {[...missionRewards, ...(intelReward > 0 ? [{ type: 'intel', amount: intelReward }] : []), ...(resourceReward ? [resourceReward] : []), ...(buildingReward ? [buildingReward] : []), ...(billyBucksReward ? [billyBucksReward] : []), ...(flashlightReward ? [flashlightReward] : [])].map((reward, index) => {
                  const isRevealed = revealedRewards[index];
                  const isIntel = reward.type === 'intel';
                  const isResource = reward.type === 'resource';
                  const isBuilding = reward.type === 'building';
                  const isBillyBucks = reward.type === 'billy_bucks';
                  const isFlashlight = reward.type === 'flashlight_color';
                  const rarityClass = !isIntel && !isResource && !isBuilding && !isBillyBucks && !isFlashlight && reward.rarity ? `rarity-${reward.rarity.toLowerCase()}` : '';
                  
                  return (
                    <div
                      key={isIntel ? 'intel' : isBuilding ? `building-${reward.data?.building_id || index}` : isBillyBucks ? 'billy_bucks' : isFlashlight ? `flashlight-${reward.data?.color || index}` : (reward.id || index)}
                      onClick={() => {
                        if (!isRevealed) {
                          setRevealedRewards(prev => {
                            const newState = [...prev];
                            newState[index] = true;
                            return newState;
                          });
                          const isLegendaryOrder = reward.rarity && String(reward.rarity).toLowerCase() === 'legendary';
                          playSoundEffect(isLegendaryOrder ? '/sounds/legendary_reveal.mp3' : '/sounds/order_reveal.mp3');
                        }
                      }}
                      style={{
                        width: '390px',
                        height: '390px',
                        margin: 0,
                        padding: 0,
                        perspective: '1000px',
                        cursor: !isRevealed ? 'pointer' : 'default'
                      }}
                    >
                      <div
                        style={{
                          width: '390px',
                          height: '390px',
                          position: 'relative',
                          transformStyle: 'preserve-3d',
                          transition: 'transform 0.6s',
                          transform: isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'
                        }}
                      >
                        {/* Front side - Question mark with video */}
                        <div style={{
                          position: 'absolute',
                          width: '390px',
                          height: '390px',
                          left: '50%',
                          transform: 'translateX(-50%) rotateY(0deg)',
                          padding: '20px 5px',
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          border: '2px solid #C0C0C0',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          boxSizing: 'border-box',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          overflow: 'hidden',
                          boxShadow: '0 0 8px rgba(200, 200, 200, 0.6), inset 0 0 10px rgba(255, 255, 255, 0.1)'
                        }}>
                          <video
                            ref={el => { videoRefs.current[index] = el; }}
                            preload="auto"
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{
                              position: 'absolute',
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              top: 0,
                              left: 0,
                              zIndex: 1,
                              pointerEvents: 'none',
                              backgroundColor: 'rgba(0, 0, 0, 0.8)'
                            }}
                            onCanPlay={() => {
                              const video = videoRefs.current[index];
                              if (video && !isRevealed) {
                                video.play().catch(err => {
                                  console.error(`Error playing video ${index}:`, err);
                                });
                              }
                            }}
                          >
                            <source src="/images/reward_animation.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                        
                        {/* Back side - Reward content */}
                        {isRevealed && (
                          <div style={{
                            position: 'absolute',
                            width: '390px',
                            height: '390px',
                            left: '50%',
                            transform: 'translateX(-50%) rotateY(180deg)',
                            padding: (isIntel || isBuilding || isBillyBucks || isFlashlight) ? '20px 5px' : '0',
                            backgroundColor: (isIntel || isBuilding || isBillyBucks || isFlashlight) ? 'rgba(0, 0, 0, 0.8)' : 'transparent',
                            border: 'none',
                            borderRadius: (isIntel || isBuilding || isBillyBucks || isFlashlight) ? '8px' : '0',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            boxSizing: 'border-box',
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden',
                            overflow: 'visible'
                          }}>
                            {isIntel ? (
                              <div className={`reward-intel-card ${rarityClass}`}>
                                <img 
                                  src="/images/reward_intel.jpg" 
                                  alt={t('blackOps.intelRewardLabel')} 
                                  className="reward-intel-image"
                                />
                                <div className="reward-intel-title">{t('blackOps.intelRewardLabel')}</div>
                                <div className="reward-intel-amount">+{reward.amount}</div>
                              </div>
                            ) : isBillyBucks ? (
                              <div className={`reward-intel-card ${rarityClass}`}>
                                <img 
                                  src="/images/billy_bucks.png" 
                                  alt="Billy Bucks" 
                                  className="reward-intel-image"
                                  onError={(e) => { e.target.src = '/images/bank.png'; e.target.alt = 'Billy Bucks'; }}
                                />
                                <div className="reward-intel-amount">+{reward.amount}</div>
                                <div style={{ fontSize: '1rem', fontWeight: 'bold', marginTop: '10px', color: '#fff' }}>Billy Bucks</div>
                              </div>
                            ) : isFlashlight ? (
                              <div className={`reward-intel-card ${rarityClass}`} style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
                                <span
                                  className="flashlight-color-swatch"
                                  style={{
                                    display: 'block',
                                    width: 120,
                                    height: 120,
                                    margin: '0 auto 16px',
                                    borderRadius: '50%',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                    background: buildFlashlightSwatchBackground(normalizeFlashlightColorForDisplay(reward.data?.color)),
                                  }}
                                  aria-hidden
                                />
                                <div style={{ fontSize: '1rem', fontWeight: 'bold' }}>{t('afterBattle.flashlightUnlocked', { color: t(`profile.flashlight.colors.${normalizeFlashlightColorForDisplay(reward.data?.color)}`) })}</div>
                              </div>
                            ) : isBuilding ? (
                              (() => {
                                const buildingId = reward.data?.building_id || '';
                                const buildingDisplayNames = {
                                  powerplant: 'Powerplant',
                                  eclipse_array: 'Eclipse Array',
                                  eclipse_array_upgraded: 'Upgraded Eclipse Array',
                                  command_center: 'Command Center',
                                  depot: 'Depot',
                                  cattle_ranch: 'Cattle Farm',
                                  cattle_ranch_upgraded: 'Upgraded Cattle Farm',
                                  quarry: 'Quarry',
                                  quarry_upgraded: 'Upgraded Quarry',
                                  sewers: 'Sewers',
                                  sewers_upgraded: 'Upgraded Sewers',
                                  portal: 'Portal',
                                  portal_upgraded: 'Upgraded Portal',
                                  lavender_field: 'Lavender Farm',
                                  lavender_field_upgraded: 'Upgraded Lavender Farm',
                                  training_grounds: 'Training Grounds',
                                  barracks: 'Barracks',
                                  armory: 'Armory',
                                  drone_hangar: 'Drone Hangar',
                                  communications_hub: 'Communications Hub',
                                  intelligence_agency: 'Intelligence Agency',
                                  bank: 'Omniforge',
                                  research_center: 'Research Center',
                                  hospital: 'Hospital',
                                  hospital_upgraded: 'Upgraded Hospital',
                                  hall: 'Hall of Ascension',
                                  hall_upgraded: 'Upgraded Hall of Ascension',
                                  airfield: 'Airfield',
                                  trade: 'Trade Forum',
                                  trade_upgraded: 'Upgraded Trade Forum',
                                  edc: 'Energy Distribution Center',
                                  edc_upgraded: 'Upgraded Energy Distribution Center'
                                };
                                const displayName = buildingDisplayNames[buildingId] || buildingId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                const buildingImages = {
                                  powerplant: '/images/powerplant.png',
                                  eclipse_array: '/images/array.png',
                                  eclipse_array_upgraded: '/images/array_upgraded.png',
                                  command_center: '/images/command.png',
                                  depot: '/images/depot.png',
                                  cattle_ranch: '/images/cattle.png',
                                  cattle_ranch_upgraded: '/images/cattle_upgraded.png',
                                  quarry: '/images/stone.png',
                                  quarry_upgraded: '/images/stone_upgraded.png',
                                  sewers: '/images/sewers.png',
                                  sewers_upgraded: '/images/sewers_upgraded.png',
                                  portal: '/images/erray.png',
                                  portal_upgraded: '/images/erray_updated.png',
                                  lavender_field: '/images/lavender.png',
                                  lavender_field_upgraded: '/images/lavender_upgraded.png',
                                  training_grounds: '/images/training.png',
                                  barracks: '/images/barracks.png',
                                  armory: '/images/armory.png',
                                  drone_hangar: '/images/dronehangar.png',
                                  communications_hub: '/images/comhub.png',
                                  intelligence_agency: '/images/intel.png',
                                  bank: '/images/bank.png',
                                  research_center: '/images/research.png',
                                  hospital: '/images/hospital.png',
                                  hospital_upgraded: '/images/hospital_upgraded.png',
                                  hall: '/images/hall.png',
                                  hall_upgraded: '/images/hall_upgraded.png',
                                  airfield: '/images/airfield.png',
                                  trade: '/images/trade.png',
                                  trade_upgraded: '/images/trade_upgraded.png',
                                  edc: '/images/edc.png',
                                  edc_upgraded: '/images/edc_upgrade.png'
                                };
                                const imgSrc = buildingImages[buildingId] || (buildingId ? `/images/${buildingId}.png` : '/images/command.png');
                                return (
                                  <div className={`reward-intel-card ${rarityClass}`} style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
                                    <img src={imgSrc} alt={displayName} className="reward-intel-image" style={{ objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                    <div style={{ fontSize: '1rem', fontWeight: 'bold', marginTop: '10px' }}>HQ Building Unlocked</div>
                                    <div style={{ fontSize: '0.9rem', color: '#ccc' }}>{displayName}</div>
                                  </div>
                                );
                              })()
                            ) : isResource ? (
                              <div className={`reward-intel-card ${rarityClass}`}>
                                {(() => {
                                  // Map resource type to image path (matching AfterBattle.js and DispatchAsset.js)
                                  const getResourceImage = (resourceType) => {
                                    if (!resourceType) {
                                      console.warn('Resource reward missing resourceType:', reward);
                                      return '/images/resource_biomass.png';
                                    }
                                    
                                    const resourceMap = {
                                      'lavender': '/images/resource_lavender.png',
                                      'biomass': '/images/resource_biomass.png',
                                      'food_rations': '/images/resource_foodrations.png',
                                      'stone': '/images/resource_stone.png',
                                      'erray': '/images/resource_erray.png'
                                    };
                                    
                                    return resourceMap[resourceType.toLowerCase()] || resourceMap[resourceType] || '/images/resource_biomass.png';
                                  };
                                  
                                  // Format resource name for display
                                  const getResourceName = (resourceType) => {
                                    if (!resourceType) return 'Resource';
                                    
                                    const nameMap = {
                                      'lavender': 'Lavender',
                                      'biomass': 'Biomass',
                                      'food_rations': 'Food Rations',
                                      'stone': 'Stone',
                                      'erray': 'Erray'
                                    };
                                    
                                    return nameMap[resourceType.toLowerCase()] || nameMap[resourceType] || resourceType;
                                  };
                                  
                                  const resourceType = reward.resourceType || 'biomass';
                                  const imagePath = getResourceImage(resourceType);
                                  const resourceName = getResourceName(resourceType);
                                  
                                  return (
                                    <>
                                      <img 
                                        src={imagePath}
                                        alt={`${resourceName} Reward`}
                                        className="reward-intel-image"
                                        onError={(e) => {
                                          // Fallback if resource image doesn't exist
                                          console.warn(`Resource image not found: ${imagePath}, falling back to biomass`);
                                          e.target.src = "/images/resource_biomass.png";
                                        }}
                                      />
                                      <div className="reward-intel-amount">+{reward.amount}</div>
                                      <div style={{ 
                                        color: '#fff', 
                                        fontSize: '16px', 
                                        marginTop: '10px',
                                        textTransform: 'capitalize'
                                      }}>
                                        {resourceName}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div style={{ 
                                width: '100%', 
                                height: '100%', 
                                position: 'relative',
                                overflow: 'visible',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                margin: 0,
                                padding: 0
                              }}>
                                <div className={`reward-order-container rarity-${(reward.rarity || 'common').toLowerCase()}`} style={{
                                  width: '390px',
                                  height: '390px',
                                  position: 'relative',
                                  margin: 0,
                                  padding: 0
                                }}>
                                  <OrderDesign
                                    order={reward}
                                    minimal={false}
                                    noMargin={true}
                                    gameState={null}
                                  />
                                  <OrderCardBubbles rarity={reward.rarity || 'common'} />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="reward-modal-footer">
              <button
                className="reward-accept-button"
                onClick={async () => {
                  setRewardModalOpen(false);
                  setMissionRewards([]);
                  setHasLegendaryReward(false);
                  setIntelReward(0);
                  setFlashlightReward(null);
                  setRevealedRewards([]);
                  // Don't clear mission outcome message - keep it visible
                  // Refresh intel after accepting rewards
                  try {
                    const intelRes = await fetch(`${API_URL}/blackops/intel`, {
                      headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                      },
                    });
                    const intelData = await intelRes.json();
                    if (intelData.currentIntel !== undefined) {
                      setCurrentIntel(intelData.currentIntel);
                    }
                    if (intelData.activeMissionCount !== undefined) {
                      setActiveMissionCount(intelData.activeMissionCount);
                    }
                    if (intelData.maxActiveMissions !== undefined) {
                      setMaxActiveMissions(intelData.maxActiveMissions);
                    }
                  } catch (err) {
                    console.error('Failed to refresh intel:', err);
                  }
                }}
              >
                {t('blackOps.accept')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mission Outcome Message - Lower Right Corner */}
      {missionOutcomeMessage && (
        <div className="mission-outcome-message">
          <div className="mission-outcome-text">
            {typeof missionOutcomeMessage === 'string' ? (
              missionOutcomeMessage
            ) : (
              <>
                {missionOutcomeMessage.isPvP && (
                  <div style={{ 
                    marginBottom: '12px', 
                    paddingBottom: '12px', 
                    borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    color: '#ffd700'
                  }}>
                    {missionOutcomeMessage.text.split('\n\n')[0]}
                  </div>
                )}
                {(missionOutcomeMessage.isPvP ? missionOutcomeMessage.text.split('\n\n').slice(1).join('\n\n') : missionOutcomeMessage.text)
                  .split(missionOutcomeMessage.outcome === 'success' ? 'successful' : 'failure').map((part, index, array) => {
                  if (index === array.length - 1) return part;
                  return (
                    <React.Fragment key={index}>
                      {part}
                      {missionOutcomeMessage.outcome === 'success' ? (
                        <span className="outcome-success">successful</span>
                      ) : (
                        <span className="outcome-failure">failure</span>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </div>
          {woundedUnits.length > 0 && (
            <div className="wounded-units-section">
              <div className="wounded-units-label">{t('blackOps.woundedUnits')}</div>
              <div className="wounded-units-list">
                {woundedUnits.map((unitName, index) => (
                  <span key={index} className="wounded-unit-name">
                    {unitName}{index < woundedUnits.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Three Order Boxes */}
      <div className="order-boxes-container">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className={`order-box ${selectedBoxIndex === index ? 'selected' : ''} ${pendingOrder ? 'active' : ''} ${index === 1 ? 'middle-box' : 'side-box'}`}
            onClick={() => {
              // Disable box clicking on responsive mode - operatives must be selected from the table
              if (layout) {
                return;
              }
              handleBoxClick(index);
            }}
          >
            {selectedOrders[index] ? (
              <div>
                <OrderDesign
                  order={selectedOrders[index]}
                  minimal={false}
                  onClick={() => {
                    if (layout) {
                      return;
                    }
                    handleBoxClick(index);
                  }}
                  noMargin={true}
                  gameState={null}
                  scale={1}
                />
              </div>
            ) : (
              <div className="order-box-placeholder">
                <span>{t('blackOps.chooseOperative')}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

      <TutorialReplayButton user={user} onReplay={() => setShowTutorialBlackOps(true)} />

      {showTutorialBlackOps && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_wait.png"
          text={t('blackOps.tutorialText')}
          continueButtonImagePath={BATTLE_UI_END_TURN_BUTTON_PATH}
          continueButtonClassName="strategy-modal-close-btn"
          paintedButtonSurfaceFilter={MENU_PAINTED_SURFACE_FILTER}
          continueLabelKey="blackOps.tutorialClose"
          onContinue={async () => {
            if (!isReplayingBlackOpsTutorial) {
              const token = localStorage.getItem('token');
              if (token) {
                await fetch(`${API_URL}/tutorial/mark-seen`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ messageKey: 'black_ops' }),
                });
                markSeenLocally('black_ops');
              }
            }
            setShowTutorialBlackOps(false);
          }}
          voiceKey="blackOps.tutorialText"
        />,
        document.body
      )}
    </div>
  );
}

export default BlackOps;

